import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Play, ArrowRight, ChevronDown, Users, Store, Globe, TrendingUp, 
  Shield, Zap, Target, MapPin, DollarSign, Calendar, Lock,
  CheckCircle, ArrowUpRight, Mail, Video, FileText, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { SEO, jsonLdSchemas } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export default function Investors() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [fundraisingConfig, setFundraisingConfig] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showDataRoomModal, setShowDataRoomModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchFundraisingConfig();
  }, []);

  const fetchFundraisingConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/investors/fundraising-config`);
      if (response.ok) {
        const data = await response.json();
        setFundraisingConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch fundraising config');
    }
  };

  // Simple product flow visualization
  const productFlow = [
    { icon: Users, label: 'U.S. Customer', sublabel: 'Creates authorization' },
    { icon: ArrowRight, label: '', sublabel: '' },
    { icon: Shield, label: 'PeCash', sublabel: 'Processes securely' },
    { icon: ArrowRight, label: '', sublabel: '' },
    { icon: Users, label: 'Family in DR', sublabel: 'Receives access' },
    { icon: ArrowRight, label: '', sublabel: '' },
    { icon: Store, label: 'Colmado', sublabel: 'Fulfills purchase' },
  ];

  return (
    <Layout>
      <SEO
        title="Investors - Cross-Border Family Commerce Opportunity"
        description="Invest in PeCash: Building a cross-border family-commerce network connecting U.S. diaspora purchasing power with Dominican neighborhood merchants."
        canonical="https://pecashglobal.com/investors"
        ogType="website"
        jsonLd={jsonLdSchemas.financeService}
      />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '32px 32px'
            }} />
          </div>
          
          <div className="max-w-6xl mx-auto px-6 relative">
            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-300 rounded-full text-sm mb-8">
                <Target className="w-4 h-4" />
                Pre-Pilot Stage
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Connecting Diaspora Purchasing Power<br />
                <span className="text-emerald-400">with Neighborhood Merchants</span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-10">
                PeCash enables U.S. customers to pay for household purchases for family members 
                at participating Dominican merchants. Beneficiaries receive purchasing access 
                for the selected merchant rather than cash.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate('/app/investor-demo')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 text-lg"
                  data-testid="watch-demo-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch PeCash Demo
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => setShowMeetingModal(true)}
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg"
                  data-testid="request-meeting-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Request Investor Meeting
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 1. The Problem */}
        <section className="py-20 bg-slate-50" data-testid="problem-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">The Problem</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Traditional remittance solutions don't address the unique needs of families managing 
                household expenses across borders.
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { title: 'Lack of Control', desc: 'Traditional remittances offer no visibility into how funds are spent' },
                  { title: 'Security Risks', desc: 'Cash pickup services create safety concerns for beneficiaries' },
                  { title: 'Merchant Gap', desc: 'Neighborhood stores lack access to digital payment infrastructure' },
                  { title: 'Diaspora Disconnect', desc: 'No way to directly support specific household needs' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                      <span className="text-red-600 font-bold">{i + 1}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 2. The Solution */}
        <section className="py-20" data-testid="solution-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">The PeCash Solution</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Merchant-anchored purchase authorizations that connect U.S. diaspora purchasing 
                power with Dominican colmados — the neighborhood stores families already trust.
              </p>
              
              {/* Product Flow Visualization */}
              <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl p-8 mb-12">
                <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
                  {productFlow.map((item, i) => (
                    item.label ? (
                      <div key={i} className="flex flex-col items-center text-center">
                        <div className="w-14 h-14 bg-white rounded-xl shadow-sm flex items-center justify-center mb-2">
                          <item.icon className="w-7 h-7 text-emerald-600" />
                        </div>
                        <span className="font-medium text-slate-900 text-sm">{item.label}</span>
                        <span className="text-slate-500 text-xs">{item.sublabel}</span>
                      </div>
                    ) : (
                      <ArrowRight key={i} className="w-5 h-5 text-emerald-400 hidden md:block" />
                    )
                  ))}
                </div>
              </div>
              
              {/* Key Benefits */}
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: Shield, title: 'Controlled Spending', desc: 'Funds can only be used at designated merchants for household goods' },
                  { icon: Zap, title: 'Instant Access', desc: 'Beneficiaries receive purchasing access immediately via secure link' },
                  { icon: Store, title: 'Local Integration', desc: 'Works with existing neighborhood merchants families already use' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 3. How It Works */}
        <section className="py-20 bg-slate-900 text-white" data-testid="how-it-works-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold mb-4 text-center">How PeCash Works</h2>
              <p className="text-slate-400 text-center max-w-3xl mx-auto mb-12">
                A simple, secure process that connects families across borders
              </p>
              
              <div className="grid md:grid-cols-4 gap-8">
                {[
                  { step: '1', title: 'Create Authorization', desc: 'Customer creates a purchase authorization for a specific amount and merchant' },
                  { step: '2', title: 'Beneficiary Receives', desc: 'Family member gets a secure link to access their purchasing authorization' },
                  { step: '3', title: 'Shop at Colmado', desc: 'Beneficiary shows QR code at participating merchant and approves purchase' },
                  { step: '4', title: 'Complete & Settle', desc: 'Merchant fulfills order, receives settlement, customer gets confirmation' },
                ].map((item, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                      {item.step}
                    </div>
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* 4. Why Dominican Republic / Why Colmados */}
        <section className="py-20" data-testid="market-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
                Why the Dominican Republic & Colmados
              </h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Strategic market entry with strong fundamentals
              </p>
              
              <div className="grid lg:grid-cols-2 gap-12">
                <div className="bg-emerald-50 rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                    <h3 className="text-xl font-bold text-slate-900">Dominican Republic</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Large U.S. diaspora community with strong family ties</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Significant remittance inflows from U.S. households</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Growing digital adoption and smartphone penetration</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Strong cultural emphasis on family support</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-blue-50 rounded-2xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Store className="w-6 h-6 text-blue-600" />
                    <h3 className="text-xl font-bold text-slate-900">Colmados</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Ubiquitous neighborhood convenience stores</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Trusted by families for daily household needs</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Underserved by digital payment solutions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">Strong owner-operated relationships with communities</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 5. Market Opportunity */}
        <section className="py-20 bg-slate-50" data-testid="opportunity-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Market Opportunity</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Targeting a large and growing market with unmet needs
              </p>
              
              <div className="bg-white rounded-2xl border border-slate-200 p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Target Market</h3>
                    <ul className="space-y-3 text-slate-600">
                      <li className="flex items-start gap-2">
                        <ChevronDown className="w-4 h-4 text-emerald-600 mt-1 rotate-[-90deg]" />
                        <span>U.S.-based Dominican diaspora sending support to families</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronDown className="w-4 h-4 text-emerald-600 mt-1 rotate-[-90deg]" />
                        <span>Families in DR seeking secure, controlled support</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronDown className="w-4 h-4 text-emerald-600 mt-1 rotate-[-90deg]" />
                        <span>Colmados seeking digital payment acceptance</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Market Size</h3>
                    <p className="text-slate-500 text-sm italic mb-4">
                      [Market size claims pending verification - data to be added]
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 text-sm">
                        Note: Specific market statistics will be added after verification 
                        from credible sources. We do not publish unverified data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 6. Business Model */}
        <section className="py-20" data-testid="business-model-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Business Model</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Clear revenue streams with room for optimization during pilot
              </p>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Service Fees</h3>
                  </div>
                  <p className="text-slate-600 mb-4">
                    Fee charged per transaction to the customer. Starts at $3.09 for $10 
                    and scales to $5.04 for $200.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">
                      Pricing structure subject to refinement based on pilot learnings
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">FX Spread</h3>
                  </div>
                  <p className="text-slate-600 mb-4">
                    Currency conversion margin on USD to DOP transactions. 
                    Competitive rates displayed at checkout.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">
                      FX strategy to be optimized based on volume and partner terms
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 7. Current Pricing */}
        <section className="py-20 bg-slate-50" data-testid="pricing-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Current Pricing</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Transparent, straightforward fee structure
              </p>
              
              <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="grid grid-cols-2 gap-px bg-slate-200">
                  <div className="bg-slate-100 px-6 py-3 font-semibold text-slate-900">Amount</div>
                  <div className="bg-slate-100 px-6 py-3 font-semibold text-slate-900">Service Fee</div>
                </div>
                {[
                  { amount: '$10', fee: '$3.09' },
                  { amount: '$20', fee: '$3.19' },
                  { amount: '$50', fee: '$3.50' },
                  { amount: '$100', fee: '$4.01' },
                  { amount: '$200', fee: '$5.04' },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-2 gap-px bg-slate-200">
                    <div className="bg-white px-6 py-3 text-slate-900">{row.amount}</div>
                    <div className="bg-white px-6 py-3 text-emerald-600 font-medium">{row.fee}</div>
                  </div>
                ))}
              </div>
              
              <p className="text-center text-sm text-slate-500 mt-6">
                Pilot limits: $10 minimum, $200 maximum per authorization
              </p>
            </motion.div>
          </div>
        </section>

        {/* 8. Competitive Positioning */}
        <section className="py-20" data-testid="competitive-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Competitive Positioning</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                A differentiated approach to cross-border family support
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold text-slate-900"></th>
                      <th className="px-6 py-4 text-center font-semibold text-emerald-600">PeCash</th>
                      <th className="px-6 py-4 text-center font-semibold text-slate-600">Traditional Remittance</th>
                      <th className="px-6 py-4 text-center font-semibold text-slate-600">Gift Cards</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: 'Spending Control', pecash: true, traditional: false, gift: 'Limited' },
                      { feature: 'Local Merchant Integration', pecash: true, traditional: false, gift: false },
                      { feature: 'No Cash Handling', pecash: true, traditional: false, gift: true },
                      { feature: 'Real-time Tracking', pecash: true, traditional: 'Limited', gift: false },
                      { feature: 'Household Focus', pecash: true, traditional: false, gift: false },
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-slate-200">
                        <td className="px-6 py-4 text-slate-900">{row.feature}</td>
                        <td className="px-6 py-4 text-center">
                          {row.pecash === true ? (
                            <CheckCircle className="w-5 h-5 text-emerald-600 mx-auto" />
                          ) : row.pecash}
                        </td>
                        <td className="px-6 py-4 text-center text-slate-400">
                          {row.traditional === true ? '✓' : row.traditional === false ? '—' : row.traditional}
                        </td>
                        <td className="px-6 py-4 text-center text-slate-400">
                          {row.gift === true ? '✓' : row.gift === false ? '—' : row.gift}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 9. Current Stage */}
        <section className="py-20 bg-emerald-900 text-white" data-testid="stage-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold mb-4 text-center">Current Stage</h2>
              <p className="text-emerald-200 text-center max-w-3xl mx-auto mb-12">
                Pre-pilot with product under final stabilization
              </p>
              
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-emerald-300">Product Development</span>
                  <span className="text-sm text-emerald-300">Pilot</span>
                  <span className="text-sm text-emerald-300">Scale</span>
                </div>
                <div className="h-2 bg-emerald-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 w-1/4 rounded-full" />
                </div>
                <div className="mt-8 grid md:grid-cols-3 gap-6">
                  <div className="bg-emerald-800/50 rounded-xl p-6">
                    <div className="text-emerald-400 font-semibold mb-2">Completed</div>
                    <ul className="text-sm text-emerald-200 space-y-2">
                      <li>• Core platform development</li>
                      <li>• Payment integration</li>
                      <li>• Merchant onboarding flow</li>
                      <li>• Security architecture</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-800/50 rounded-xl p-6 border-2 border-emerald-400">
                    <div className="text-emerald-400 font-semibold mb-2">In Progress</div>
                    <ul className="text-sm text-emerald-200 space-y-2">
                      <li>• Final product stabilization</li>
                      <li>• Investor demo preparation</li>
                      <li>• Initial merchant recruitment</li>
                      <li>• Compliance documentation</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-800/50 rounded-xl p-6">
                    <div className="text-emerald-400 font-semibold mb-2">Next</div>
                    <ul className="text-sm text-emerald-200 space-y-2">
                      <li>• Controlled pilot launch</li>
                      <li>• Merchant network expansion</li>
                      <li>• Performance optimization</li>
                      <li>• Unit economics validation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 10. Long-term Vision */}
        <section className="py-20" data-testid="vision-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Long-term Vision</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Building toward a cross-border family-commerce network
              </p>
              
              <div className="bg-gradient-to-r from-slate-900 to-emerald-900 rounded-2xl p-8 md:p-12 text-white">
                <div className="max-w-3xl mx-auto text-center">
                  <Globe className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
                  <p className="text-xl md:text-2xl leading-relaxed">
                    PeCash is starting with Dominican colmados but is building toward a 
                    <span className="text-emerald-400 font-semibold"> cross-border family-commerce network </span>
                    connecting diaspora purchasing power with the neighborhood merchants families already use.
                  </p>
                  <p className="text-emerald-300 text-sm mt-6 italic">
                    This is a long-term vision, not a claim about current scale.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 11. Team */}
        <section className="py-20 bg-slate-50" data-testid="team-section">
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Team</h2>
              <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                Dedicated to making cross-border family support simple and secure
              </p>
              
              <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <div className="w-24 h-24 bg-slate-200 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Users className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">[Founder Name]</h3>
                <p className="text-emerald-600 mb-4">Founder & CEO</p>
                <p className="text-slate-600 text-sm mb-6">
                  [PLACEHOLDER: Founder bio and background to be added]
                </p>
                <a href="#" className="text-emerald-600 hover:text-emerald-700 text-sm inline-flex items-center gap-1">
                  LinkedIn <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 12. Fundraising Section */}
        {fundraisingConfig && (
          <section className="py-20" data-testid="fundraising-section">
            <div className="max-w-6xl mx-auto px-6">
              <motion.div {...fadeInUp}>
                <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Fundraising</h2>
                <p className="text-slate-600 text-center max-w-3xl mx-auto mb-12">
                  Seeking partners who share our vision for family-commerce
                </p>
                
                <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 p-8">
                  <div className="space-y-6">
                    {fundraisingConfig.targetRaiseVisible && (
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Target Raise</span>
                        <span className="font-semibold text-slate-900">{fundraisingConfig.targetRaise}</span>
                      </div>
                    )}
                    
                    {fundraisingConfig.structureVisible && (
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Structure</span>
                        <span className="font-semibold text-slate-900">{fundraisingConfig.structure}</span>
                      </div>
                    )}
                    
                    {fundraisingConfig.valuationCapVisible && (
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Valuation Cap</span>
                        <span className="font-semibold text-slate-900">{fundraisingConfig.valuationCap}</span>
                      </div>
                    )}
                    
                    {fundraisingConfig.amountCommittedVisible && (
                      <div className="flex justify-between items-center py-3 border-b border-slate-100">
                        <span className="text-slate-600">Amount Committed</span>
                        <span className="font-semibold text-emerald-600">{fundraisingConfig.amountCommitted}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-500 text-center mt-6 italic">
                    Terms subject to negotiation and legal review
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-slate-900 to-emerald-900" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Learn More?
              </h2>
              <p className="text-slate-300 mb-10 max-w-2xl mx-auto">
                Watch our product demo, schedule an investor meeting, or request access 
                to our data room for detailed financials and documentation.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate('/app/investor-demo')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Watch Demo
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => setShowMeetingModal(true)}
                  className="border-white/30 text-white hover:bg-white/10 px-8"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Request Meeting
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => setShowDataRoomModal(true)}
                  className="border-white/30 text-white hover:bg-white/10 px-8"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Data Room Access
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Meeting Request Modal */}
        {showMeetingModal && (
          <MeetingRequestModal onClose={() => setShowMeetingModal(false)} />
        )}
        
        {/* Data Room Access Modal */}
        {showDataRoomModal && (
          <DataRoomRequestModal onClose={() => setShowDataRoomModal(false)} />
        )}
      </div>
    </Layout>
  );
}

function MeetingRequestModal({ onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', firm: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/investors/meeting-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit meeting request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        {isSubmitted ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Received</h3>
            <p className="text-slate-600 mb-6">We'll be in touch shortly to schedule your meeting.</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-slate-900 mb-4">Request Investor Meeting</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                required
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="email"
                placeholder="Email"
                required
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="text"
                placeholder="Firm (Optional)"
                value={formData.firm}
                onChange={e => setFormData(p => ({ ...p, firm: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                placeholder="Tell us about your interest in PeCash"
                rows={3}
                value={formData.message}
                onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 resize-none"
              />
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function DataRoomRequestModal({ onClose }) {
  const [formData, setFormData] = useState({ name: '', email: '', firm: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/investors/dataroom-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Failed to submit data room request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        {isSubmitted ? (
          <div className="text-center py-8">
            <Lock className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Received</h3>
            <p className="text-slate-600 mb-6">We'll review your request and grant access if approved.</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Data Room Access</h3>
            <p className="text-slate-600 text-sm mb-4">
              Access to detailed financials, projections, and confidential documents requires approval.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Your Name"
                required
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="email"
                placeholder="Email"
                required
                value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <input
                type="text"
                placeholder="Firm / Organization"
                required
                value={formData.firm}
                onChange={e => setFormData(p => ({ ...p, firm: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                placeholder="Why are you requesting access?"
                rows={3}
                required
                value={formData.reason}
                onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500 resize-none"
              />
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {isSubmitting ? 'Submitting...' : 'Request Access'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
